import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { getApiUrl } from '../../config/api';
import SessionTemplates from './SessionTemplates';
import './ResearchSessionManager.css';

const ResearchSessionManager = ({ 
  selectedBrands, 
  dateRange, 
  useAllTime, 
  researchNotes, 
  onSessionLoad,
  onSessionSave 
}) => {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [sessionName, setSessionName] = useState('');
  const [sessionDescription, setSessionDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showTemplates, setShowTemplates] = useState(false);

  // Get auth headers
  const getAuthHeaders = useCallback(async () => {
    if (!user) return {};
    
    try {
      const token = await user.getIdToken();
      return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
    } catch (error) {
      console.error('Failed to get auth token:', error);
      return {};
    }
  }, [user]);

  // Load user's research sessions
  const loadSessions = useCallback(async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const headers = await getAuthHeaders();
      
      const response = await fetch(`${getApiUrl('api/analytics/research-sessions')}`, {
        headers
      });
      
      if (response.ok) {
        const data = await response.json();
        setSessions(data.sessions || []);
      }
    } catch (error) {
      console.error('Error loading sessions:', error);
      setError('Failed to load research sessions');
    } finally {
      setLoading(false);
    }
  }, [user, getAuthHeaders]);

  // Save current research session
  const saveSession = useCallback(async (name, description) => {
    if (!user || !name.trim()) return;
    
    try {
      setLoading(true);
      const headers = await getAuthHeaders();
      
      const sessionData = {
        name: name.trim(),
        description: description.trim(),
        selectedBrands,
        dateRange,
        useAllTime,
        researchNotes,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      const response = await fetch(`${getApiUrl('api/analytics/research-sessions')}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(sessionData)
      });
      
      if (response.ok) {
        const data = await response.json();
        setSessions(prev => [data.session, ...prev]);
        setCurrentSession(data.session);
        setShowSessionModal(false);
        setSessionName('');
        setSessionDescription('');
        onSessionSave && onSessionSave(data.session);
      } else {
        throw new Error('Failed to save session');
      }
    } catch (error) {
      console.error('Error saving session:', error);
      setError('Failed to save research session');
    } finally {
      setLoading(false);
    }
  }, [user, selectedBrands, dateRange, useAllTime, researchNotes, getAuthHeaders, onSessionSave]);

  // Load a specific research session
  const loadSession = useCallback(async (sessionId) => {
    if (!user) return;
    
    try {
      setLoading(true);
      const headers = await getAuthHeaders();
      
      const response = await fetch(`${getApiUrl('api/analytics/research-sessions')}/${sessionId}`, {
        headers
      });
      
      if (response.ok) {
        const data = await response.json();
        setCurrentSession(data.session);
        onSessionLoad && onSessionLoad(data.session);
      } else {
        throw new Error('Failed to load session');
      }
    } catch (error) {
      console.error('Error loading session:', error);
      setError('Failed to load research session');
    } finally {
      setLoading(false);
    }
  }, [user, getAuthHeaders, onSessionLoad]);

  // Delete a research session
  const deleteSession = useCallback(async (sessionId) => {
    if (!user || !window.confirm('Are you sure you want to delete this research session?')) return;
    
    try {
      setLoading(true);
      const headers = await getAuthHeaders();
      
      const response = await fetch(`${getApiUrl('api/analytics/research-sessions')}/${sessionId}`, {
        method: 'DELETE',
        headers
      });
      
      if (response.ok) {
        setSessions(prev => prev.filter(s => s.id !== sessionId));
        if (currentSession && currentSession.id === sessionId) {
          setCurrentSession(null);
        }
      } else {
        throw new Error('Failed to delete session');
      }
    } catch (error) {
      console.error('Error deleting session:', error);
      setError('Failed to delete research session');
    } finally {
      setLoading(false);
    }
  }, [user, currentSession, getAuthHeaders]);

  // Update current session
  const updateCurrentSession = useCallback(async () => {
    if (!user || !currentSession) return;
    
    try {
      setLoading(true);
      const headers = await getAuthHeaders();
      
      const sessionData = {
        ...currentSession,
        selectedBrands,
        dateRange,
        useAllTime,
        researchNotes,
        updatedAt: new Date().toISOString()
      };
      
      const response = await fetch(`${getApiUrl('api/analytics/research-sessions')}/${currentSession.id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(sessionData)
      });
      
      if (response.ok) {
        const data = await response.json();
        setCurrentSession(data.session);
        setSessions(prev => prev.map(s => s.id === currentSession.id ? data.session : s));
      } else {
        throw new Error('Failed to update session');
      }
    } catch (error) {
      console.error('Error updating session:', error);
      setError('Failed to update research session');
    } finally {
      setLoading(false);
    }
  }, [user, currentSession, selectedBrands, dateRange, useAllTime, researchNotes, getAuthHeaders]);

  // Load sessions on component mount
  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  // Auto-save current session when data changes
  useEffect(() => {
    if (currentSession && (selectedBrands.length > 0 || researchNotes.trim())) {
      const timeoutId = setTimeout(() => {
        updateCurrentSession();
      }, 2000); // Auto-save after 2 seconds of inactivity
      
      return () => clearTimeout(timeoutId);
    }
  }, [currentSession, selectedBrands, researchNotes, updateCurrentSession]);

  const handleSaveNewSession = () => {
    if (!sessionName.trim()) {
      setError('Please enter a session name');
      return;
    }
    saveSession(sessionName, sessionDescription);
  };

  const handleTemplateSelect = (template) => {
    setSessionName(template.name);
    setSessionDescription(template.description);
    setShowTemplates(false);
    setShowSessionModal(true);
    
    // Apply template data to the parent component
    onSessionLoad && onSessionLoad({
      selectedBrands: template.suggestedBrands,
      dateRange: template.dateRange,
      useAllTime: template.useAllTime,
      researchNotes: template.researchNotes
    });
  };

  const handleLoadSession = (session) => {
    loadSession(session.id);
  };

  const handleDeleteSession = (session) => {
    deleteSession(session.id);
  };

  const handleExportSession = (session) => {
    const exportData = {
      sessionName: session.name,
      description: session.description,
      selectedBrands: session.selectedBrands,
      dateRange: session.dateRange,
      useAllTime: session.useAllTime,
      researchNotes: session.researchNotes,
      exportedAt: new Date().toISOString(),
      exportedBy: user?.email || 'Unknown'
    };

    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `${session.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_research_session.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleShareSession = async (session) => {
    try {
      const shareData = {
        title: `Research Session: ${session.name}`,
        text: `Check out this research session: ${session.description}`,
        url: window.location.href
      };

      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        // Fallback: copy to clipboard
        const shareText = `${shareData.title}\n\n${shareData.text}\n\n${shareData.url}`;
        await navigator.clipboard.writeText(shareText);
        alert('Session details copied to clipboard!');
      }
    } catch (error) {
      console.error('Error sharing session:', error);
      alert('Failed to share session');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="research-session-manager">
      <div className="session-header">
        <h3>Research Sessions</h3>
        <div className="session-actions">
          <button 
            className="templates-btn"
            onClick={() => setShowTemplates(!showTemplates)}
            disabled={loading}
          >
            {showTemplates ? 'Hide' : 'Show'} Templates
          </button>
          <button 
            className="new-session-btn"
            onClick={() => setShowSessionModal(true)}
            disabled={loading}
          >
            New Session
          </button>
          {currentSession && (
            <button 
              className="update-session-btn"
              onClick={updateCurrentSession}
              disabled={loading}
            >
              Save Changes
            </button>
          )}
        </div>
      </div>

      {currentSession && (
        <div className="current-session">
          <h4>Current Session: {currentSession.name}</h4>
          {currentSession.description && (
            <p className="session-description">{currentSession.description}</p>
          )}
          <div className="session-meta">
            <span>Last updated: {formatDate(currentSession.updatedAt)}</span>
            <span>Brands: {currentSession.selectedBrands?.length || 0}</span>
          </div>
        </div>
      )}

      {/* Session Templates */}
      {showTemplates && (
        <SessionTemplates onTemplateSelect={handleTemplateSelect} />
      )}

      <div className="session-history">
        <h4>Recent Sessions</h4>
        {loading && <div className="loading">Loading sessions...</div>}
        {error && <div className="error">{error}</div>}
        
        {sessions.length === 0 && !loading ? (
          <div className="no-sessions">
            <p>No research sessions yet. Create your first session to get started!</p>
          </div>
        ) : (
          <div className="sessions-list">
            {sessions.map((session) => (
              <div 
                key={session.id} 
                className={`session-item ${currentSession?.id === session.id ? 'active' : ''}`}
              >
                <div className="session-info">
                  <h5>{session.name}</h5>
                  {session.description && (
                    <p className="session-desc">{session.description}</p>
                  )}
                  <div className="session-meta">
                    <span>Created: {formatDate(session.createdAt)}</span>
                    <span>Brands: {session.selectedBrands?.length || 0}</span>
                  </div>
                </div>
                <div className="session-actions">
                  <button 
                    className="load-session-btn"
                    onClick={() => handleLoadSession(session)}
                    disabled={loading}
                  >
                    Load
                  </button>
                  <button 
                    className="export-session-btn"
                    onClick={() => handleExportSession(session)}
                    disabled={loading}
                    title="Export session data"
                  >
                    Export
                  </button>
                  <button 
                    className="share-session-btn"
                    onClick={() => handleShareSession(session)}
                    disabled={loading}
                    title="Share session"
                  >
                    Share
                  </button>
                  <button 
                    className="delete-session-btn"
                    onClick={() => handleDeleteSession(session)}
                    disabled={loading}
                    title="Delete session"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* New Session Modal */}
      {showSessionModal && (
        <div className="session-modal-overlay">
          <div className="session-modal">
            <h3>Create New Research Session</h3>
            <div className="modal-content">
              <div className="form-group">
                <label htmlFor="sessionName">Session Name *</label>
                <input
                  id="sessionName"
                  type="text"
                  value={sessionName}
                  onChange={(e) => setSessionName(e.target.value)}
                  placeholder="e.g., Q4 Holiday Analysis, Nike vs Adidas Study"
                  maxLength={100}
                />
              </div>
              <div className="form-group">
                <label htmlFor="sessionDescription">Description (Optional)</label>
                <textarea
                  id="sessionDescription"
                  value={sessionDescription}
                  onChange={(e) => setSessionDescription(e.target.value)}
                  placeholder="Brief description of this research session..."
                  maxLength={500}
                  rows={3}
                />
              </div>
            </div>
            <div className="modal-actions">
              <button 
                className="cancel-btn"
                onClick={() => {
                  setShowSessionModal(false);
                  setSessionName('');
                  setSessionDescription('');
                  setError(null);
                }}
              >
                Cancel
              </button>
              <button 
                className="save-btn"
                onClick={handleSaveNewSession}
                disabled={loading || !sessionName.trim()}
              >
                {loading ? 'Saving...' : 'Create Session'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResearchSessionManager;
